/// <amd-module name="Reporting/Samples/ReportingSamples" />
import * as React from "react";

import * as Resources from "Reporting/Samples/Resources";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Button } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { Header, TitleSize } from "VSSUI/Header";
import { VssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { Page } from "VSSUI/Page";
import { Spinner } from "VSSUI/Spinner";
import { ITabProviderContext, Tab, TabBar, TabProviderContext } from "VSSUI/Tabs";
import { TextField } from "VSSUI/TextField";

import { AnalyticsODataVersion, BatchRequestMode, ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { IAnalyticsODataClient } from "Analytics/Common/RestClient/AnalyticsODataClient";

import { ICacheableQueryService } from "Reporting/Common/CacheableQueryService";
import { ICacheableQuery } from "Reporting/Common/ICacheableQuery";

import { ProjectsQuery } from "Analytics/Queries/ProjectsQuery";
import { ITfsPageService } from "Tfs/Platform/Page";
import { VisualsCommonSamples } from "./VisualsCommonSamples";
import { VisualsSpecializedSamples } from "./VisualsSpecializedSamples";

import { latestODataVersion } from "Analytics/Common/Utilities/QueryUtilities";

interface IReportingSamplesProps {}

interface IReportingSamplesState {
    queryResult?: any;
    isRunningQuery?: boolean;
    scriptContent?: any;
}

/**
 * This sample provides a limited gallery experience to review available Reporting SDK features.
 */
class ReportingSamples extends VssComponent<IReportingSamplesProps, IReportingSamplesState> {
    private static _odataClientTabId = "analyticsOdataClient";
    private static _cacheableQueriesTabId = "cacheableQueries";
    private static _odataSandboxTabId = "interactiveOdataEditor";
    private static _visualsCommonTabId = "visualsCommon";
    private static _visualsSpecializedTabId = "visualsSpecialized";
    private _loggedPerfEvent: boolean;
    private _hubViewState: VssHubViewState;

    constructor(props: IReportingSamplesProps, context: IVssComponentContext) {
        super(props, context);

        this._hubViewState = new VssHubViewState(context.pageContext, {
            defaultPivot: ReportingSamples._odataClientTabId
        });

        this.state = this.getDefaultState();
    }

    public componentDidMount(): void {
        this._hubViewState.selectedPivot.subscribe(this.pivotChange);
    }

    public componentWillUnmount(): void {
        this._hubViewState.selectedPivot.unsubscribe(this.pivotChange);
    }

    public componentDidUpdate(prevProps: IReportingSamplesProps, prevState: IReportingSamplesState): void {
        super.componentDidUpdate(prevProps, prevState);

        if (!this._loggedPerfEvent) {
            const perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            perfService.markTimeToInteractive("tfs", "WebPlatform.Samples.ReportingSamples");
            this._loggedPerfEvent = true;
        }
    }

    public render(): JSX.Element {
        return (
            <Page selectedTabId={this._hubViewState.selectedPivot} className="flex-grow reporting-samples-container">
                <Header className="flex-noshrink" title={Resources.Title} titleSize={TitleSize.Medium} />
                <TabBar onSelectedTabChanged={this.onSelectedTabChanged}>
                    <Tab id={ReportingSamples._odataClientTabId} name={Resources.AnalyticsClientPivot} />
                    <Tab id={ReportingSamples._cacheableQueriesTabId} name={Resources.CacheableQueriesPivot} />
                    <Tab id={ReportingSamples._odataSandboxTabId} name={Resources.ODataSandboxPivot} />
                    <Tab id={ReportingSamples._visualsCommonTabId} name={Resources.VisualsCommonPivot} />
                    <Tab id={ReportingSamples._visualsSpecializedTabId} name={Resources.VisualsSpecializedPivot} />
                </TabBar>
                <TabProviderContext.Consumer>
                    {(tabProviderContext: ITabProviderContext) => {
                        if (tabProviderContext.selectedId === ReportingSamples._odataClientTabId) {
                            return (
                                <div className="page-content page-content-top">
                                    <div>
                                        <b>IAnalyticsODataClient</b> client is used for conveying OData requests in web query scenarios.
                                    </div>
                                    <br />
                                    <div>
                                        <Button text={Resources.RunProjectQueryText} onClick={this.runProjectQuery.bind(this)} />
                                        <Button text={Resources.RunProcessesQueryText} onClick={this.runProcessesQuery.bind(this)} />
                                        <Button text={Resources.RunTagsBatchQueryText} onClick={this.runTagsBatchQuery.bind(this)} />
                                        <Button text={Resources.RunPagedQueryText} onClick={this.runPagedQuery.bind(this)} />
                                    </div>
                                    <FormItem label="Snippet">
                                        <TextField multiline rows={20} value={this.state.scriptContent} readOnly={true} />
                                    </FormItem>
                                    {this.getGeneralQueryUsage()}
                                    {this.renderQueryResult(this.state.isRunningQuery)}
                                </div>
                            );
                        }
                        if (tabProviderContext.selectedId === ReportingSamples._cacheableQueriesTabId) {
                            return (
                                <div className="page-content page-content-top">
                                    <div>
                                        <b>ICacheableQueryService</b> provides a central facility for short-lived reuse of information during a page
                                        view in Reporting and Dashboard scenarios. In particular, they are heavily used for reuse of data in
                                        configuration/picker scenarios as well as for re-rendering of previously selected states during configuration.
                                        The page-level feature experience is responsible for clearing state on the service. <br />
                                        <b>ICacheableQuery</b> implementations encapsulate requests for service data including REST and OData
                                        endpoints. The input options and output formats of cached queries are geared for simple consumption, with
                                        strongly typed, scenario-relevant abstractions. Anotherconcern cachable query implementations address is
                                        driving key-recognition of semantically equivalent requests for caching through a singular codepath.
                                    </div>
                                    <br />
                                    <div>
                                        <Button text={Resources.RunProjectCachedQueryText} onClick={this.runCachedQuery.bind(this)} />
                                        <Button text={Resources.ResetCachedQueryServiceText} onClick={this.clearCache.bind(this)} />
                                    </div>
                                    <FormItem label="Snippet">
                                        <TextField multiline rows={20} value={this.state.scriptContent} readOnly={true} />
                                    </FormItem>
                                    {this.renderQueryResult(this.state.isRunningQuery)}
                                </div>
                            );
                        }
                        if (tabProviderContext.selectedId === ReportingSamples._odataSandboxTabId) {
                            return (
                                <div className="page-content page-content-top">
                                    <div>This sandbox is provided for ad-hoc evaluation and testing of OData query options.</div>
                                    <br />
                                    <FormItem label="Editor">
                                        <TextField multiline rows={20} value={this.state.scriptContent} onChange={this.updateScriptContent} />
                                    </FormItem>
                                    <Button text={Resources.RunSandboxQueryText} onClick={this.runCustomQuery.bind(this)} primary={true} />
                                    {this.state.isRunningQuery ? (
                                        <Spinner />
                                    ) : (
                                        <FormItem label="Result">
                                            <TextField
                                                multiline
                                                rows={20}
                                                value={JSON.stringify(this.state.queryResult, null, " ")}
                                                readOnly={true}
                                            />
                                        </FormItem>
                                    )}
                                </div>
                            );
                        }
                        if (tabProviderContext.selectedId === ReportingSamples._visualsCommonTabId) {
                            return (
                                <div className="page-content page-content-top">
                                    <VisualsCommonSamples />
                                </div>
                            );
                        }
                        if (tabProviderContext.selectedId === ReportingSamples._visualsSpecializedTabId) {
                            return (
                                <div className="page-content">
                                    <VisualsSpecializedSamples />
                                </div>
                            );
                        }
                    }}
                </TabProviderContext.Consumer>
            </Page>
        );
    }

    private onSelectedTabChanged = (newTabId: string) => {
        this._hubViewState.selectedPivot.value = newTabId;
    };

    private renderQueryResult(isLoading: boolean | undefined): JSX.Element {
        return isLoading ? (
            <Spinner />
        ) : (
            <FormItem label="Result">
                <TextField multiline rows={20} value={JSON.stringify(this.state.queryResult, null, " ")} readOnly={true} />
            </FormItem>
        );
    }

    private getDefaultState(): IReportingSamplesState {
        return {
            queryResult: null,
            isRunningQuery: false,
            scriptContent: "Click a button to run an operation."
        };
    }

    private pivotChange = (): void => {
        if (this._hubViewState.selectedPivot.value === ReportingSamples._odataSandboxTabId) {
            this.resetSandbox();
        } else {
            this.setState(this.getDefaultState());
        }
    };

    private updateScriptContent = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, scriptContent: string): void => {
        this.setState({
            scriptContent: scriptContent
        });
    };

    private setStateFromStartingQuery(scriptContent: Function): void {
        this.setState({ isRunningQuery: true, scriptContent: scriptContent ? scriptContent.toString() : "", queryResult: null });
    }

    private setStateFromQueryResult(result: any): void {
        this.setState({ isRunningQuery: false, queryResult: result });
    }

    private setStateFromFailure(error: any): void {
        this.setStateFromQueryResult(`Cached Query failed due to: ${error}`);
    }

    private runProjectQuery(): void {
        // Directly run OData query
        const queryOptions = {
            entityType: "Projects",
            oDataVersion: latestODataVersion,
            $orderby: "ProjectName",
            $select: "ProjectId,ProjectName"
        };

        this.setStateFromStartingQuery(this.runProjectQuery);
        this.wrappedODataQueryCall(queryOptions);
    }

    private runProcessesQuery(): void {
        // Directly run OData query
        const queryOptions = {
            entityType: "Processes",
            oDataVersion: latestODataVersion,
            $apply: `groupby((BacklogCategoryReferenceName, WorkItemType, IsBugType, BacklogName))`
        };
        this.setStateFromStartingQuery(this.runProcessesQuery);
        this.wrappedODataQueryCall(queryOptions);
    }

    private runTagsBatchQuery(): void {
        // Directly run OData query
        const queryOptions = {
            entityType: "Tags",
            oDataVersion: latestODataVersion,
            $orderby: "TagName",
            $select: "TagId, TagName",

            batchRequestMode: BatchRequestMode.Enabled
        };

        this.setStateFromStartingQuery(this.runTagsBatchQuery);
        this.wrappedODataQueryCall(queryOptions);
    }

    private runPagedQuery(): void {
        // Directly run OData query
        const queryOptions = {
            entityType: "Tags",
            oDataVersion: latestODataVersion,
            $orderby: "TagName",
            $select: "TagId, TagName",

            maxPageSize: 10 /** This is artificially low to force-demonstrate paging. */,
            followNextLink: true
        };

        this.setStateFromStartingQuery(this.runPagedQuery);
        this.wrappedODataQueryCall(queryOptions);
    }

    private async wrappedODataQueryCall(queryOptions: ODataQueryOptions): Promise<void> {
        const client = this.context.pageContext.getRestClient<IAnalyticsODataClient>("IAnalyticsODataClient");
        try {
            const extendedOptions = queryOptions;
            extendedOptions.pageContext = this.context.pageContext;

            // Scope requests to current project.
            const pageServiceData = this.context.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
            const projectName = pageServiceData && pageServiceData.project ? pageServiceData.project.name : undefined;
            extendedOptions.project = projectName;

            const result = await client.query("EngineeringSamplePage", queryOptions);
            this.setStateFromQueryResult(result);
        } catch (error) {
            this.setStateFromFailure(error);
        }
    }

    private getGeneralQueryUsage(): JSX.Element {
        const queryUsageText =
            'import { AnalyticsODataVersion, ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";\n' +
            'import { IAnalyticsODataClient } from "Analytics/Common/RestClient/AnalyticsODataClient";\n' +
            "\n...\n\n" +
            'const client = this.context.pageContext.getRestClient<IAnalyticsODataClient>("IAnalyticsODataClient");\n' +
            "try {\n" +
            '    const result = await client.query("FeatureExperienceName", queryOptions);\n' +
            "    // Use the result\n" +
            "}\n" +
            "catch (error) {\n" +
            "   // Act on the error\n" +
            "}\n";
        return (
            <FormItem label="wrappedOdataQueryCall() snippet in async-await style">
                <TextField multiline rows={14} value={queryUsageText} readOnly={true} />
            </FormItem>
        );
    }

    private runCachedQuery(): void {
        const projectsQuery = ProjectsQuery.onCollection();
        this.setStateFromStartingQuery(this.runCachedQuery);
        this.wrappedCacheServiceCall(projectsQuery);
    }

    private async wrappedCacheServiceCall<T>(cacheableQuery: ICacheableQuery<T>): Promise<void> {
        const cacheableQueryService = this.context.pageContext.getService<ICacheableQueryService>("ICacheableQueryService");
        try {
            const result = await cacheableQueryService.getCacheableQueryResult(ProjectsQuery.onCollection());
            this.setStateFromQueryResult(result);
        } catch (error) {
            this.setStateFromFailure(error);
        }
    }

    private clearCache(): void {
        // Clear query cache & reset UI State
        const cacheableQueryService = this.context.pageContext.getService<ICacheableQueryService>("ICacheableQueryService");
        this.setStateFromStartingQuery(this.clearCache);
        cacheableQueryService.resetCache();
        this.setStateFromQueryResult("Query cache cleared");
    }

    private resetSandbox(): void {
        this.setState({
            queryResult: "Click the button to run query for the OData configuration above",
            isRunningQuery: false,
            scriptContent: JSON.stringify(
                {
                    oDataVersion: AnalyticsODataVersion.v1,
                    entityType: "Projects",
                    $orderby: "ProjectName",
                    $select: "ProjectId, ProjectName",
                    batchRequestMode: BatchRequestMode.Enabled
                },
                null,
                "  "
            )
        });
    }

    private runCustomQuery(): void {
        try {
            const parsedQuery = JSON.parse(this.state.scriptContent);
            this.setState({ isRunningQuery: true, queryResult: null });
            this.wrappedODataQueryCall(parsedQuery);
        } catch (error) {
            this.setStateFromFailure("Custom Query could not be executed");
        }
    }
}

Components.add("reporting-sample-view", ReportingSamples);
